import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaEllipsisV,
  FaPaperPlane,
  FaSpinner,
  FaStar,
  FaUser,
} from "react-icons/fa";
import getImageUrl from "../../../utils/imageUtils";
import { isCurrentUserMessage } from "./messageUtils";
import MessageContent from "./MessageContent";
import styles from "../../../styles/components/profile/MessagesPanel.module.css";

const MessageThread = ({
  selectedConversation,
  messages,
  loadingMessages,
  messageText,
  sending,
  currentUser,
  typingUsers,
  isMarketplaceScope,
  messagesListRef,
  onBack,
  onMessageInputChange,
  onSendMessage,
  reviewEligible,
  showHeaderMenu,
  onLeaveReview,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  return (
    <div className={styles.messagesPanel}>
      <div className={styles.messagesHeader}>
        <button
          type="button"
          className={styles.backButton}
          onClick={onBack}
          aria-label="Back to conversations"
        >
          <FaArrowLeft />
        </button>

        <div className={styles.messagesHeaderMain}>
          {(selectedConversation.otherUser?._id ||
            selectedConversation.otherUser) && (
            <>
              <Link
                to={`/user/${
                  selectedConversation.otherUser?._id ||
                  selectedConversation.otherUser
                }`}
                className={styles.messagesHeaderUser}
              >
                {selectedConversation.otherUser?.avatar ? (
                  <img
                    src={getImageUrl(selectedConversation.otherUser.avatar)}
                    alt={selectedConversation.otherUser.firstName || "User"}
                    className={styles.headerAvatar}
                  />
                ) : (
                  <div className={styles.headerAvatarPlaceholder}>
                    <FaUser />
                  </div>
                )}
                <div className={styles.messagesHeaderText}>
                  <div className={styles.headerNameRow}>
                    <h3>
                      {selectedConversation.otherUser?.firstName || ""}{" "}
                      {selectedConversation.otherUser?.lastName || ""}
                    </h3>
                  </div>
                  {selectedConversation.otherUser?.username && (
                    <p className={styles.headerUsername}>
                      @{selectedConversation.otherUser.username}
                    </p>
                  )}
                </div>
              </Link>
              {isMarketplaceScope && selectedConversation.listing?.title && (
                <Link
                  to={`/marketplace/${
                    selectedConversation.listing._id ||
                    selectedConversation.listing
                  }`}
                  className={styles.listingBadge}
                >
                  Re: {selectedConversation.listing.title}
                </Link>
              )}
            </>
          )}
        </div>

        {showHeaderMenu && (
          <div className={styles.headerMenuContainer} ref={menuRef}>
            <button
              type="button"
              className={styles.headerMenuButton}
              onClick={() => setShowMenu((open) => !open)}
              aria-label="Conversation options"
              aria-expanded={showMenu}
            >
              <FaEllipsisV />
            </button>
            {showMenu && (
              <div className={styles.headerMenuDropdown}>
                {reviewEligible ? (
                  <button
                    type="button"
                    className={styles.headerMenuItem}
                    onClick={() => {
                      setShowMenu(false);
                      onLeaveReview?.();
                    }}
                  >
                    <FaStar /> Leave a Review
                  </button>
                ) : (
                  <p className={styles.headerMenuHint}>
                    Chat more with the seller to unlock reviews
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.messagesList} ref={messagesListRef}>
        {loadingMessages && messages.length === 0 ? (
          <div className={styles.loading}>Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((message) => {
              const isOwnMessage = isCurrentUserMessage(message, currentUser);

              return (
                <div
                  key={message._id}
                  className={`${styles.messageItem} ${
                    isOwnMessage ? styles.ownMessage : ""
                  }`}
                >
                  {!isOwnMessage && (
                    <div className={styles.messageAvatar}>
                      {message.sender.avatar ? (
                        <img
                          src={getImageUrl(message.sender.avatar)}
                          alt={message.sender.firstName}
                        />
                      ) : (
                        <FaUser />
                      )}
                    </div>
                  )}
                  <div className={styles.messageContent}>
                    {!isOwnMessage && (
                      <div className={styles.messageSender}>
                        {message.sender.firstName} {message.sender.lastName}
                      </div>
                    )}
                    <div className={styles.messageBubble}>
                      <MessageContent text={message.text} />
                      <div className={styles.messageMeta}>
                        <span className={styles.messageTime}>
                          {new Date(message.createdAt).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "numeric",
                              minute: "2-digit",
                            },
                          )}
                        </span>
                        {isOwnMessage && (
                          <span
                            className={`${styles.readIndicator} ${
                              message.read
                                ? styles.readIndicatorRead
                                : styles.readIndicatorSent
                            }`}
                            title={message.read ? "Read" : "Sent"}
                          >
                            {message.read ? "✓✓" : "✓"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {Object.keys(typingUsers).length > 0 && (
              <div className={styles.typingIndicator}>
                <div className={styles.typingDots}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className={styles.typingText}>
                  {Object.values(typingUsers).join(", ")} typing...
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {reviewEligible && (
        <div className={styles.reviewBanner}>
          <span className={styles.reviewBannerText}>
            <FaStar className={styles.reviewBannerStar} />
            How was your experience with{" "}
            <strong>
              {selectedConversation.listing?.seller?.firstName ||
                selectedConversation.otherUser?.firstName ||
                "this seller"}
            </strong>
            ?
          </span>
          <button
            type="button"
            className={styles.reviewBannerBtn}
            onClick={onLeaveReview}
          >
            Leave a Review
          </button>
        </div>
      )}

      <form className={styles.messageForm} onSubmit={onSendMessage}>
        <input
          type="text"
          value={messageText}
          onChange={onMessageInputChange}
          placeholder="Type a message..."
          className={styles.messageInput}
          maxLength={2000}
        />
        <button
          type="submit"
          disabled={!messageText.trim() || sending}
          className={styles.sendButton}
        >
          {sending ? (
            <FaSpinner className={styles.spinner} />
          ) : (
            <FaPaperPlane />
          )}
        </button>
      </form>
    </div>
  );
};

export default MessageThread;
